import { defineStore } from 'pinia'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
import { grpcCall } from '@/grpc-client/grpc/grpc-call'
import { $grpcClient } from '@/grpc-client/grpc-client'
import { grpcReq } from '@/grpc-client/grpc/grpc-request'
import { EventLogger } from '@/utils/EventLogger'
import { grpcErrorMessage } from '@/utils/grpcErrorMessage'
import { ERouteNames } from '@/router/ERouteNames'

import type { IAdminStoreState } from '@/modules/admin/types/IAdminStoreState'
import type { IUpdateUserPayload } from '@/modules/admin/types/UpdateUserPayload'
import type { ISearchedUser } from '@/modules/admin/types/ISearchedUser'
import type { ICreateServerPayload } from '@/modules/admin/types/ICreateServerPayload'
import type { IUpdateServerPayload } from '@/modules/admin/types/IUpdateServerPayload'
// Типизация payload для создания тарифа
import type { ICreateTariffPayload } from '@/modules/admin/types/ICreateTariffPayload'
// Типизация payload для обновления тарифа
import type { IUpdateTariffPayload } from '@/modules/admin/types/IUpdateTariffPayload'
// Типизация тарифного плана
import type { ITariff } from '@/modules/api/types/ITariff'
import type { ICreateSimpleTransactionPayload } from '@/modules/admin/types/ICreateSimpleTransactionPayload'
import type { CreateSimpleTransactionRequest, CancelTransactionRequest, TransactionFilters, MoveTransactionRequest } from '@vpn-tg/vue-client-grpc/proto/transaction/transaction-endpoint'
import type { SendMailingRequest } from '@vpn-tg/vue-client-grpc/proto/mailing/mailing-endpoint'
import { PaymentStatus } from '@vpn-tg/vue-client-grpc/proto/enums/transactionStatus'
import { MailingMethod, MailingUserFilter } from '@vpn-tg/vue-client-grpc/proto/mailing/mailing-endpoint'


export const useAdminStore = defineStore('adminStore', {
  state: (): IAdminStoreState => ({
    foundUser: {
      id: '',
      isAdmin: false,
      telegramID: '',
      referralLink: '',
      isTariffActive: false,
      referralCount: 0,
      referralBonusDaysCount: 0,
      lastTariffExpiresAt: null,
      lastTariffID: undefined,
      nextChargeAt: null,
      firstName: '',
      lastName: '',
      isBlocked: false,
      isWebInterfaceOpened: false,
      description: '',
      hasUsedPromo: false,
      createdAt: { seconds: '', nanos: 0 },
      lastSeenAt: { seconds: '', nanos: 0 },
    },
    isSearched: false,
    servers: [],
    tariffs: [],
    users: [],
    transactions: [],
    guides: [],
    isServiceWorksNoticeVisible: false,
    serviceWorksNoticeText: '',
    adminOptions: [
      { name: 'Серверы', route: ERouteNames.SERVERS },
      { name: 'Юзеры', route: ERouteNames.USERS },
      { name: 'Блок рассылок', route: ERouteNames.MESSAGES },
      { name: 'Тех работы', route: ERouteNames.WORKS },
      { name: 'Инструкции', route: ERouteNames.GUIDES },
      { name: 'Транзакции', route: ERouteNames.TRANSACTIONS },
      { name: 'Настройки', route: ERouteNames.SETTINGS },
      { name: 'Статистика', route: ERouteNames.STATISTICS },
      { name: 'Консоль', route: ERouteNames.CONSOLE },
    ],
  }),
  actions: {
    async getServers() {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true

      try {
        const [ok, response] = await grpcCall(
          (_args, options) => $grpcClient.ServerEndpoint.getServers(grpcReq({}), options),
          'getServers',
        )

        if (!ok || !response) {
          EventLogger.error('getServers', ['Не удалось выполнить запрос через gRPC'])
          return false
        }

        console.log('getServers ответ', response)

        if (response.error) {
          EventLogger.error('getServers', [
            `Ошибка: ${response.error.message}`,
            `Детали: ${response.error.systemMessage}`,
          ])
          feedbackStore.showToast({
            type: 'error',
            message: grpcErrorMessage(response.error, 'errors.tryLater'),
          })
          return false
        }

        this.servers = response.servers || []

        // Логируем только если серверов нет, но это не ошибка
        if (response.servers && response.servers.length === 0) {
          console.log('Список серверов пуст')
        }

        return true
      } catch (error: unknown) {
        console.error('getServers CATCH:', error)
        EventLogger.error('getServers', [`Ошибка getServers: ${(error as Error).message}`])
        return false
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    async getUsers(searchString: string = '') {
      this.isSearched = true

      try {
        // Fix TS2345: ids обязателен в типе UserFilters, даже если пустой
        const filters = {
          ids: [],
          ...(searchString ? { searchString } : {}),
        }

        const [ok, response] = await grpcCall(
          (_args, options) =>
            $grpcClient.UserEndpoint.getUsers(
              grpcReq({
                filters: filters,
                pagination: { page: 1, size: 50 },
                sorts: [],
              }),
              options,
            ),
          'getUsers',
        )

        if (!ok || response?.error) {
          EventLogger.error('getUsers', ['Ошибка получения пользователей', response?.error])
          return false
        }

        const rawUsers = response.users || []

        this.users = rawUsers.map((u) => {
          const lastTariffExpiresAt = u.lastTariffExpiresAt
            ? Number(u.lastTariffExpiresAt.seconds)
            : null

          const user: ISearchedUser = {
            id: u.id,
            isAdmin: u.isAdmin || false,
            telegramID: u.telegramID || '',
            referralLink: u.referralLink || '',
            isTariffActive: u.isTariffActive || false,
            referralCount: u.referralCount || 0,
            referralBonusDaysCount: u.referralBonusDaysCount || 0,
            lastTariffExpiresAt: lastTariffExpiresAt,
            lastTariffID: u.lastTariffID,
            nextChargeAt: null,

            firstName: u.firstName || '',
            lastName: u.lastName || '',
            isBlocked: u.isBlocked || false,
            isWebInterfaceOpened: u.isWebInterfaceOpened || false,
            description: u.description || '',
            referrerID: u.referrerID, // Опциональное поле string
            email: u.email || '', // Подтверждённая электронная почта пользователя
            newEmail: u.newEmail || '', // Неподтверждённый email — ожидает верификации по magic link
            hasUsedPromo: u.hasUsedPromo || false,

            createdAt: u.createdAt
              ? { seconds: Number(u.createdAt.seconds), nanos: u.createdAt.nanos }
              : { seconds: 0, nanos: 0 },

            lastSeenAt: u.lastSeenAt
              ? { seconds: Number(u.lastSeenAt.seconds), nanos: u.lastSeenAt.nanos }
              : { seconds: 0, nanos: 0 },
          }
          return user
        })

        return true
      } catch (error) {
        EventLogger.error('getUsers', [`Catch: ${(error as Error).message}`])
        return false
      } finally {
        this.isSearched = false
      }
    },

    async updateUser(updateData: IUpdateUserPayload): Promise<boolean> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true

      try {
        const requestData = {
          id: updateData.id,
          ...(updateData.referrerID !== undefined && { referrerID: updateData.referrerID }),
          ...(updateData.firstName !== undefined && { firstName: updateData.firstName }),
          ...(updateData.lastName !== undefined && { lastName: updateData.lastName }),
          ...(updateData.isBlocked !== undefined && { isBlocked: updateData.isBlocked }),
          ...(updateData.isAdmin !== undefined && { isAdmin: updateData.isAdmin }),
          ...(updateData.description !== undefined && { description: updateData.description }),
          ...(updateData.isWebInterfaceOpened !== undefined && {
            isWebInterfaceOpened: updateData.isWebInterfaceOpened,
          }),
          ...(updateData.email !== undefined && { email: updateData.email }),
          ...(updateData.telegramID !== undefined && { telegramID: String(updateData.telegramID) }),
        }

        const [ok, response] = await grpcCall(
          (_, options) => $grpcClient.UserEndpoint.updateUser(grpcReq(requestData), options),
          'updateUser',
        )

        if (response?.error) {
          EventLogger.error('updateUser', ['Ошибка при обновлении пользователя', response?.error])
          feedbackStore.showToast({
            type: 'error',
            message: grpcErrorMessage(response.error, 'errors.tryLater'),
          })
          return false
        }

        if (!ok) {
          EventLogger.error('updateUser', ['Ошибка при обновлении пользователя'])
          return false
        }

        // Обновляем в списке пользователей
        const userIndex = this.users.findIndex((u) => u.id === updateData.id)
        if (userIndex !== -1) {
          const updateFields: Partial<ISearchedUser> = {}
          if (updateData.firstName !== undefined) updateFields.firstName = updateData.firstName
          if (updateData.lastName !== undefined) updateFields.lastName = updateData.lastName
          if (updateData.isBlocked !== undefined) updateFields.isBlocked = updateData.isBlocked
          if (updateData.isAdmin !== undefined) updateFields.isAdmin = updateData.isAdmin
          if (updateData.description !== undefined)
            updateFields.description = updateData.description
          if (updateData.referrerID !== undefined) updateFields.referrerID = updateData.referrerID
          if (updateData.isWebInterfaceOpened !== undefined) {
            updateFields.isWebInterfaceOpened = updateData.isWebInterfaceOpened
          }
          if (updateData.email !== undefined) updateFields.email = updateData.email
          if (updateData.telegramID !== undefined) updateFields.telegramID = updateData.telegramID
          Object.assign(this.users[userIndex], updateFields)
        }

        // Если редактируемый пользователь сейчас открыт в детальном просмотре (foundUser), обновляем и его для синхронизации
        if (this.foundUser.id === updateData.id) {
          const updateFields: Partial<ISearchedUser> = {}
          if (updateData.firstName !== undefined) updateFields.firstName = updateData.firstName
          if (updateData.lastName !== undefined) updateFields.lastName = updateData.lastName
          if (updateData.isBlocked !== undefined) updateFields.isBlocked = updateData.isBlocked
          if (updateData.isAdmin !== undefined) updateFields.isAdmin = updateData.isAdmin
          if (updateData.description !== undefined)
            updateFields.description = updateData.description
          if (updateData.isWebInterfaceOpened !== undefined) {
            updateFields.isWebInterfaceOpened = updateData.isWebInterfaceOpened
          }
          if (updateData.email !== undefined) updateFields.email = updateData.email
          if (updateData.telegramID !== undefined) updateFields.telegramID = updateData.telegramID
          Object.assign(this.foundUser, updateFields)
        }

        feedbackStore.showToast({
          type: 'success',
          message: 'Данные пользователя обновлены',
        })
        return true
      } catch (error) {
        return false
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * Удаляет пользователя по ID через UserEndpoint.deleteUser
     * После успешного удаления убирает пользователя из локального списка
     *
     * @param {string} id - ID пользователя для удаления
     * @returns {Promise<boolean>} true если удаление прошло успешно
     */
    async deleteUser(id: string): Promise<boolean> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true

      try {
        // 1. Отправляем запрос на удаление пользователя
        const [ok, response] = await grpcCall(
          (_, options) => $grpcClient.UserEndpoint.deleteUser(grpcReq({ id }), options),
          'deleteUser',
        )

        // 2. Обработка ошибки от сервера
        if (response?.error) {
          EventLogger.error('deleteUser', ['Ошибка удаления пользователя', response?.error])
          feedbackStore.showToast({
            type: 'error',
            message: grpcErrorMessage(response.error, 'errors.tryLater'),
          })
          return false
        }

        if (!ok) {
          EventLogger.error('deleteUser', ['Ошибка удаления пользователя'])
          return false
        }

        // 3. Убираем пользователя из локального списка
        this.users = this.users.filter((u) => u.id !== id)
        feedbackStore.showToast({ type: 'success', message: 'Пользователь удален' })
        return true
      } catch (error) {
        EventLogger.error('deleteUser', [`Catch: ${(error as Error).message}`])
        return false
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    async createServer(payload: ICreateServerPayload): Promise<boolean> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true

      try {
        const [ok, response] = await grpcCall(
          (_, options) => $grpcClient.ServerEndpoint.createServer(grpcReq(payload), options),
          'createServer',
        )

        if (response?.error) {
          EventLogger.error('createServer', ['Ошибка создания сервера', response?.error])
          feedbackStore.showToast({
            type: 'error',
            message: grpcErrorMessage(response.error, 'errors.tryLater'),
          })
          return false
        }

        if (!ok) {
          EventLogger.error('createServer', ['Ошибка создания сервера'])
          return false
        }

        feedbackStore.showToast({ type: 'success', message: 'Сервер успешно создан' })
        // Перезапрашиваем список для актуализации данных и получения ID
        await this.getServers()
        return true
      } catch (error) {
        EventLogger.error('createServer', [`Catch: ${(error as Error).message}`])
        return false
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    async updateServer(payload: IUpdateServerPayload): Promise<boolean> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true

      try {
        const [ok, response] = await grpcCall(
          (_, options) => $grpcClient.ServerEndpoint.updateServer(grpcReq(payload), options),
          'updateServer',
        )

        if (response?.error) {
          EventLogger.error('updateServer', ['Ошибка обновления сервера', response?.error])
          feedbackStore.showToast({
            type: 'error',
            message: grpcErrorMessage(response.error, 'errors.tryLater'),
          })
          return false
        }

        if (!ok) {
          EventLogger.error('updateServer', ['Ошибка обновления сервера'])
          return false
        }

        // При изменении inboundID бэк пересчитывает vlessQuery и port — перезагружаем список
        if (payload.inboundID !== undefined) {
          await this.getServers()
        } else {
          // Локальное обновление стейта
          const serverIndex = this.servers.findIndex((s) => s.id === payload.id)
          if (serverIndex !== -1) {
            // Логика Single Default: если текущий стал дефолтным, сбрасываем у остальных
            if (payload.isDefault) {
              this.servers.forEach((s) => {
                if (s.id !== payload.id) s.isDefault = false
              })
            }

            Object.assign(this.servers[serverIndex], payload)
          }
        }

        feedbackStore.showToast({ type: 'success', message: 'Сервер обновлен' })
        return true
      } catch (error) {
        EventLogger.error('updateServer', [`Catch: ${(error as Error).message}`])
        return false
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    async deleteServer(id: string): Promise<boolean> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true

      try {
        const [ok, response] = await grpcCall(
          (_, options) => $grpcClient.ServerEndpoint.deleteServer(grpcReq({ id }), options),
          'deleteServer',
        )

        if (response?.error) {
          EventLogger.error('deleteServer', ['Ошибка удаления', response?.error])
          feedbackStore.showToast({
            type: 'error',
            message: grpcErrorMessage(response.error, 'errors.tryLater'),
          })
          return false
        }

        if (!ok) {
          EventLogger.error('deleteServer', ['Ошибка удаления'])
          return false
        }

        this.servers = this.servers.filter((s) => s.id !== id)
        feedbackStore.showToast({ type: 'success', message: 'Сервер удален' })
        return true
      } catch (error) {
        EventLogger.error('deleteServer', [`Catch: ${(error as Error).message}`])
        return false
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * Загружает список всех тарифов с сервера
     *
     * Основные действия:
     * - Отправляет запрос через TariffEndpoint.getTariffs
     * - Обновляет стейт this.tariffs полученным списком
     *
     * @returns {Promise<boolean>} Успешность загрузки
     */
    async getTariffs(): Promise<boolean> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true

      try {
        const [ok, response] = await grpcCall(
          (_args, options) => $grpcClient.TariffEndpoint.getTariffs(grpcReq({}), options),
          'getTariffs',
        )

        if (!ok || !response) {
          EventLogger.error('getTariffs', ['Не удалось выполнить запрос через gRPC'])
          return false
        }

        if (response.error) {
          EventLogger.error('getTariffs', [
            `Ошибка: ${response.error.message}`,
            `Детали: ${response.error.systemMessage}`,
          ])
          feedbackStore.showToast({
            type: 'error',
            message: grpcErrorMessage(response.error, 'errors.tryLater'),
          })
          return false
        }

        this.tariffs = (response.tariffs || []) as ITariff[]
        return true
      } catch (error: unknown) {
        EventLogger.error('getTariffs', [`Ошибка getTariffs: ${(error as Error).message}`])
        return false
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * Создаёт новый тариф
     *
     * Основные действия:
     * - Отправляет запрос через TariffEndpoint.createTariff
     * - Перезагружает список тарифов при успехе
     *
     * @param {ICreateTariffPayload} payload - Данные нового тарифа
     * @returns {Promise<boolean>} Успешность создания
     */
    async createTariff(payload: ICreateTariffPayload): Promise<boolean> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true

      try {
        const [ok, response] = await grpcCall(
          (_, options) =>
            $grpcClient.TariffEndpoint.createTariff(
              grpcReq({ ...payload, currency: 1 }),
              options,
            ),
          'createTariff',
        )

        if (response?.error) {
          EventLogger.error('createTariff', ['Ошибка создания тарифа', response?.error])
          feedbackStore.showToast({
            type: 'error',
            message: grpcErrorMessage(response.error, 'errors.tryLater'),
          })
          return false
        }

        if (!ok) {
          EventLogger.error('createTariff', ['Ошибка создания тарифа'])
          return false
        }

        feedbackStore.showToast({ type: 'success', message: 'Тариф успешно создан' })
        // Перезапрашиваем список для актуализации данных и получения ID
        await this.getTariffs()
        return true
      } catch (error) {
        EventLogger.error('createTariff', [`Catch: ${(error as Error).message}`])
        return false
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * Обновляет существующий тариф
     *
     * Основные действия:
     * - Отправляет запрос через TariffEndpoint.updateTariff
     * - Локально обновляет тариф в стейте при успехе
     *
     * @param {IUpdateTariffPayload} payload - Данные для обновления тарифа
     * @returns {Promise<boolean>} Успешность обновления
     */
    async updateTariff(payload: IUpdateTariffPayload): Promise<boolean> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true

      try {
        const [ok, response] = await grpcCall(
          (_, options) => $grpcClient.TariffEndpoint.updateTariff(grpcReq(payload), options),
          'updateTariff',
        )

        if (response?.error) {
          EventLogger.error('updateTariff', ['Ошибка обновления тарифа', response?.error])
          feedbackStore.showToast({
            type: 'error',
            message: grpcErrorMessage(response.error, 'errors.tryLater'),
          })
          return false
        }

        if (!ok) {
          EventLogger.error('updateTariff', ['Ошибка обновления тарифа'])
          return false
        }

        // Локальное обновление стейта
        const tariffIndex = this.tariffs.findIndex((t) => t.id === payload.id)
        if (tariffIndex !== -1) {
          Object.assign(this.tariffs[tariffIndex], payload)
        }

        feedbackStore.showToast({ type: 'success', message: 'Тариф обновлён' })
        return true
      } catch (error) {
        EventLogger.error('updateTariff', [`Catch: ${(error as Error).message}`])
        return false
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * Удаляет тариф по идентификатору
     *
     * Основные действия:
     * - Отправляет запрос через TariffEndpoint.deleteTariff
     * - Удаляет тариф из локального стейта при успехе
     *
     * @param {string} id - Идентификатор тарифа для удаления
     * @returns {Promise<boolean>} Успешность удаления
     */
    async deleteTariff(id: string): Promise<boolean> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true

      try {
        const [ok, response] = await grpcCall(
          (_, options) => $grpcClient.TariffEndpoint.deleteTariff(grpcReq({ id }), options),
          'deleteTariff',
        )

        if (response?.error) {
          EventLogger.error('deleteTariff', ['Ошибка удаления', response?.error])
          feedbackStore.showToast({
            type: 'error',
            message: grpcErrorMessage(response.error, 'errors.tryLater'),
          })
          return false
        }

        if (!ok) {
          EventLogger.error('deleteTariff', ['Ошибка удаления'])
          return false
        }

        this.tariffs = this.tariffs.filter((t) => t.id !== id)
        feedbackStore.showToast({ type: 'success', message: 'Тариф удалён' })
        return true
      } catch (error) {
        EventLogger.error('deleteTariff', [`Catch: ${(error as Error).message}`])
        return false
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * Добавляет дни к подписке пользователя через CreateSimpleTransaction
     *
     * Основные действия:
     * - Формирует типизированный payload CreateSimpleTransactionRequest
     * - Отправляет запрос через TransactionEndpoint.createSimpleTransaction
     *
     * @param {ICreateSimpleTransactionPayload} data - Идентификатор пользователя и количество дней
     * @returns {Promise<boolean>} Успешность операции
     */
    async createSimpleTransaction(data: ICreateSimpleTransactionPayload): Promise<boolean> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true

      try {
        // 1. Формируем типизированный запрос
        const payload: CreateSimpleTransactionRequest = {
          accessToken: '',
          userID: data.userID,
          durationDays: data.durationDays,
        }

        // 2. Отправляем запрос на добавление дней
        const [ok, response] = await grpcCall(
          (_args, options) =>
            $grpcClient.TransactionEndpoint.createSimpleTransaction(grpcReq(payload), options),
          'createSimpleTransaction',
        )

        // 3. Обработка ошибки от сервера
        if (response?.error) {
          EventLogger.error('createSimpleTransaction', ['Ошибка', response?.error])
          feedbackStore.showToast({
            type: 'error',
            message: grpcErrorMessage(response.error, 'errors.tryLater'),
          })
          return false
        }

        if (!ok) {
          EventLogger.error('createSimpleTransaction', ['Не удалось добавить дни'])
          return false
        }

        feedbackStore.showToast({ type: 'success', message: 'Дни добавлены к подписке' })
        return true
      } catch (error) {
        EventLogger.error('createSimpleTransaction', [`Catch: ${(error as Error).message}`])
        return false
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * Отменяет транзакцию и выполняет возврат через CancelTransaction
     *
     * Основные действия:
     * - Отправляет запрос через TransactionEndpoint.cancelTransaction
     * - При успехе обновляет статус транзакции в локальном массиве на PaymentRefunded
     *
     * @param {string} id - Идентификатор транзакции для отмены
     * @returns {Promise<boolean>} Успешность операции
     */
    async cancelTransaction(id: string): Promise<boolean> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true

      try {
        // 1. Формируем типизированный запрос
        const payload: CancelTransactionRequest = {
          accessToken: '',
          id,
        }

        // 2. Отправляем запрос на отмену транзакции
        const [ok, response] = await grpcCall(
          (_args, options) =>
            $grpcClient.TransactionEndpoint.cancelTransaction(grpcReq(payload), options),
          'cancelTransaction',
        )

        // 3. Обработка ошибки от сервера
        if (response?.error) {
          EventLogger.error('cancelTransaction', ['Ошибка', response?.error])
          feedbackStore.showToast({
            type: 'error',
            message: grpcErrorMessage(response.error, 'errors.tryLater'),
          })
          return false
        }

        if (!ok) {
          EventLogger.error('cancelTransaction', ['Не удалось отменить транзакцию'])
          return false
        }

        // 4. Локальное обновление статуса транзакции для мгновенного отклика UI
        const txIndex = this.transactions.findIndex((t) => t.id === id)
        if (txIndex !== -1) {
          this.transactions[txIndex].paymentStatus = PaymentStatus.PaymentRefunded
        }

        feedbackStore.showToast({ type: 'success', message: 'Транзакция отменена' })
        return true
      } catch (error) {
        EventLogger.error('cancelTransaction', [`Catch: ${(error as Error).message}`])
        return false
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * Загружает список всех инструкций (гайдов) с сервера
     *
     * Основные действия:
     * - Отправляет запрос через GuideEndpoint.getGuides
     * - Обновляет стейт this.guides полученным списком
     *
     * @returns {Promise<boolean>} Успешность загрузки
     */
    async getGuides(): Promise<boolean> {
      try {
        const [ok, response] = await grpcCall(
          (_args, options) => $grpcClient.GuideEndpoint.getGuides(grpcReq({}), options),
          'getGuides',
        )

        if (!ok || !response || response.error) {
          EventLogger.error('getGuides', ['Не удалось загрузить гайды', response?.error])
          return false
        }

        this.guides = response.guides || []
        return true
      } catch (error) {
        EventLogger.error('getGuides', [`Catch: ${(error as Error).message}`])
        return false
      }
    },

    /**
     * Получает файл и текст конкретной инструкции по идентификатору
     *
     * @param {string} id - Идентификатор гайда
     * @returns Файл, текст и имя или null при ошибке
     */
    async getGuide(
      id: string,
    ): Promise<{ pdfFile: Uint8Array; text: string; name: string } | null> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true

      try {
        const [ok, response] = await grpcCall(
          (_args, options) =>
            $grpcClient.GuideEndpoint.getGuide(grpcReq({ id }), options),
          'getGuide',
        )

        if (ok && response && response.guide) {
          return {
            pdfFile: response.guide.pdfFile,
            text: response.guide.text || '',
            name: response.guide.name || '',
          }
        }

        if (response?.error) {
          feedbackStore.showToast({
            type: 'error',
            message: grpcErrorMessage(response.error, 'errors.tryLater'),
          })
        }
        return null
      } catch (error) {
        EventLogger.error('getGuide', [`Catch: ${(error as Error).message}`])
        return null
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * Создаёт новую инструкцию с названием, файлом и текстом
     *
     * @param {string} name - Название инструкции
     * @param {Uint8Array} pdfFile - Байты файла изображения
     * @param {string} [text] - Произвольный текст инструкции
     * @returns {Promise<boolean>} Успешность создания
     */
    async createGuide(name: string, pdfFile: Uint8Array, text = ''): Promise<boolean> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true

      try {
        const [ok, response] = await grpcCall(
          (_args, options) =>
            $grpcClient.GuideEndpoint.createGuide(
              grpcReq({ name, pdfFile, text }),
              options,
            ),
          'createGuide',
        )

        if (response?.error) {
          EventLogger.error('createGuide', ['Ошибка создания гайда', response?.error])
          feedbackStore.showToast({
            type: 'error',
            message: grpcErrorMessage(response.error, 'errors.tryLater'),
          })
          return false
        }

        if (!ok) {
          EventLogger.error('createGuide', ['Ошибка создания гайда'])
          return false
        }

        feedbackStore.showToast({ type: 'success', message: 'Инструкция загружена' })
        await this.getGuides()
        return true
      } catch (error) {
        EventLogger.error('createGuide', [`Catch: ${(error as Error).message}`])
        return false
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * Удаляет инструкцию по идентификатору
     *
     * Основные действия:
     * - Отправляет запрос через GuideEndpoint.deleteGuide
     * - Перезагружает список гайдов при успехе
     *
     * @param {string} id - Идентификатор гайда для удаления
     * @returns {Promise<boolean>} Успешность удаления
     */
    async deleteGuide(id: string): Promise<boolean> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true

      try {
        const [ok, response] = await grpcCall(
          (_args, options) =>
            $grpcClient.GuideEndpoint.deleteGuide(grpcReq({ id }), options),
          'deleteGuide',
        )

        if (response?.error) {
          EventLogger.error('deleteGuide', ['Ошибка удаления', response?.error])
          feedbackStore.showToast({
            type: 'error',
            message: grpcErrorMessage(response.error, 'errors.tryLater'),
          })
          return false
        }

        if (!ok) {
          EventLogger.error('deleteGuide', ['Ошибка удаления'])
          return false
        }

        feedbackStore.showToast({ type: 'success', message: 'Инструкция удалена' })
        await this.getGuides()
        return true
      } catch (error) {
        EventLogger.error('deleteGuide', [`Catch: ${(error as Error).message}`])
        return false
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * Обновляет существующую инструкцию через GuideEndpoint.updateGuide (PATCH-семантика)
     *
     * @param {string} id - Идентификатор гайда
     * @param {string} [name] - Новое название (опционально)
     * @param {Uint8Array} [pdfFile] - Новый файл (опционально)
     * @param {string} [text] - Новый текст (опционально)
     * @returns {Promise<boolean>} Успешность обновления
     */
    async updateGuide(
      id: string,
      name?: string,
      pdfFile?: Uint8Array,
      text?: string,
    ): Promise<boolean> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true

      try {
        const payload: { id: string; name?: string; pdfFile?: Uint8Array; text?: string } = { id }
        if (name !== undefined) payload.name = name
        if (pdfFile !== undefined) payload.pdfFile = pdfFile
        if (text !== undefined) payload.text = text

        const [ok, response] = await grpcCall(
          (_args, options) =>
            $grpcClient.GuideEndpoint.updateGuide(
              grpcReq(payload),
              options,
            ),
          'updateGuide',
        )

        if (response?.error) {
          EventLogger.error('updateGuide', ['Ошибка обновления', response?.error])
          feedbackStore.showToast({
            type: 'error',
            message: grpcErrorMessage(response.error, 'errors.tryLater'),
          })
          return false
        }

        if (!ok) {
          EventLogger.error('updateGuide', ['Ошибка обновления'])
          return false
        }

        feedbackStore.showToast({ type: 'success', message: 'Инструкция обновлена' })
        await this.getGuides()
        return true
      } catch (error) {
        EventLogger.error('updateGuide', [`Catch: ${(error as Error).message}`])
        return false
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * Отправляет массовую рассылку сообщений пользователям
     *
     * Основные действия:
     * - Формирует типизированный payload SendMailingRequest
     * - Отправляет через MailingEndpoint.sendMailing
     * - Возвращает количество отправленных сообщений
     *
     * @param {MailingUserFilter[]} filters - Группы целевой аудитории
     * @param {string} text - Текст рассылки
     * @param {MailingMethod[]} methods - Список методов рассылки (Telegram, Email)
     * @param {string} subject - Тема письма (для EMAIL)
     * @returns {Promise<number | null>} Количество отправленных сообщений или null при ошибке
     */
    async sendMailing(
      filters: MailingUserFilter[],
      text: string,
      methods: MailingMethod[] = [MailingMethod.TELEGRAM_MESSAGE],
      subject: string = '',
    ): Promise<number | null> {
      const feedbackStore = useFeedbackStore()

      feedbackStore.isGlobalLoading = true
      try {
        const payload: SendMailingRequest = {
          accessToken: '',
          userIDs: [],
          filters,
          text,
          methods,
          subject,
        }

        const [ok, response] = await grpcCall(
          (_args, options) =>
            $grpcClient.MailingEndpoint.sendMailing(grpcReq(payload), options),
          'sendMailing',
        )

        if (response?.error) {
          EventLogger.error('sendMailing', ['Ошибка рассылки', response?.error])
          feedbackStore.showToast({
            type: 'error',
            message: grpcErrorMessage(response.error, 'errors.tryLater'),
          })
          return null
        }

        if (!ok) {
          EventLogger.error('sendMailing', ['Ошибка рассылки'])
          return null
        }

        feedbackStore.showToast({
          type: 'success',
          message: `Отправлено сообщений: ${response.sentCount}`,
        })
        return response.sentCount
      } catch (error) {
        EventLogger.error('sendMailing', [`Catch: ${(error as Error).message}`])
        return null
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * Загружает список транзакций с сервера с возможностью фильтрации
     *
     * Основные действия:
     * - Отправляет запрос через TransactionEndpoint.getTransactions
     * - Обновляет стейт this.transactions полученным списком
     *
     * @param {Partial<TransactionFilters>} filters - Фильтры (ids, userIDs, paymentStatuses)
     * @returns {Promise<boolean>} Успешность загрузки
     */
    async getTransactions(filters?: Partial<TransactionFilters>): Promise<boolean> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true

      try {
        const requestFilters = {
          ids: [],
          userIDs: [],
          paymentStatuses: [],
          ...filters,
        }

        const [ok, response] = await grpcCall(
          (_args, options) =>
            $grpcClient.TransactionEndpoint.getTransactions(
              grpcReq({ filters: requestFilters }),
              options,
            ),
          'getTransactions',
        )

        if (!ok || !response) {
          EventLogger.error('getTransactions', ['Не удалось выполнить запрос через gRPC'])
          return false
        }

        if (response.error) {
          EventLogger.error('getTransactions', [
            `Ошибка: ${response.error.message}`,
            `Детали: ${response.error.systemMessage}`,
          ])
          feedbackStore.showToast({
            type: 'error',
            message: grpcErrorMessage(response.error, 'errors.tryLater'),
          })
          return false
        }

        // Сортировка по дате создания — новые транзакции первыми
        const txList = response.transactions || []
        txList.sort((a, b) => {
          const aTime = a.createdAt ? Number(a.createdAt.seconds) : 0
          const bTime = b.createdAt ? Number(b.createdAt.seconds) : 0
          return bTime - aTime
        })
        this.transactions = txList
        return true
      } catch (error: unknown) {
        EventLogger.error('getTransactions', [
          `Ошибка getTransactions: ${(error as Error).message}`,
        ])
        return false
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * Поиск транзакций по универсальному ID — параллельно ищет по ID транзакции и по ID пользователя,
     * объединяет результаты с дедупликацией
     *
     * @param searchId — строка поиска (может быть ID транзакции или ID пользователя)
     * @param statuses — фильтр статусов оплаты
     * @returns {Promise<boolean>} — успех операции
     */
    async searchTransactionsById(
      searchId: string,
      statuses: PaymentStatus[],
    ): Promise<boolean> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true

      try {
        // Общий фильтр статусов для обоих запросов
        const statusFilter = statuses.length > 0 ? statuses : []

        // 1. Параллельный поиск по ID транзакции и по ID пользователя
        const [txByIdResult, txByUserResult] = await Promise.all([
          grpcCall(
            (_args, options) =>
              $grpcClient.TransactionEndpoint.getTransactions(
                grpcReq({
                  filters: { ids: [searchId], userIDs: [], paymentStatuses: statusFilter },
                }),
                options,
              ),
            'searchByTransactionId',
          ),
          grpcCall(
            (_args, options) =>
              $grpcClient.TransactionEndpoint.getTransactions(
                grpcReq({
                  filters: { ids: [], userIDs: [searchId], paymentStatuses: statusFilter },
                }),
                options,
              ),
            'searchByUserId',
          ),
        ])

        // 2. Объединение результатов с дедупликацией по ID транзакции
        const txMap = new Map<string, (typeof this.transactions)[number]>()

        for (const [ok, response] of [txByIdResult, txByUserResult]) {
          if (ok && response && !response.error) {
            for (const tx of response.transactions || []) {
              txMap.set(tx.id, tx)
            }
          }
        }

        // 3. Сортировка по дате создания — новые транзакции первыми
        const merged = [...txMap.values()]
        merged.sort((a, b) => {
          const aTime = a.createdAt ? Number(a.createdAt.seconds) : 0
          const bTime = b.createdAt ? Number(b.createdAt.seconds) : 0
          return bTime - aTime
        })

        this.transactions = merged
        return true
      } catch (error: unknown) {
        EventLogger.error('searchTransactionsById', [
          `Ошибка searchTransactionsById: ${(error as Error).message}`,
        ])
        return false
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * Переносит транзакцию на другого пользователя через MoveTransaction
     *
     * @param transactionID - Идентификатор переносимой транзакции
     * @param targetUserID - ID целевого пользователя
     * @returns {Promise<boolean>} Успешность операции
     */
    async moveTransaction(transactionID: string, targetUserID: string): Promise<boolean> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true

      try {
        const payload: MoveTransactionRequest = {
          accessToken: '',
          transactionID,
          targetUserID,
        }

        const [ok, response] = await grpcCall(
          (_args, options) =>
            $grpcClient.TransactionEndpoint.moveTransaction(grpcReq(payload), options),
          'moveTransaction',
        )

        if (response?.error) {
          EventLogger.error('moveTransaction', ['Ошибка переноса транзакции', response?.error])
          feedbackStore.showToast({
            type: 'error',
            message: grpcErrorMessage(response.error, 'errors.tryLater'),
          })
          return false
        }

        if (!ok) {
          EventLogger.error('moveTransaction', ['Не удалось перенести транзакцию'])
          return false
        }

        // Обновляем список транзакций (или убираем перенесенную/обновляем её userID локально)
        const txIndex = this.transactions.findIndex((t) => t.id === transactionID)
        if (txIndex !== -1) {
          this.transactions[txIndex].userID = targetUserID
        }

        feedbackStore.showToast({ type: 'success', message: 'Транзакция успешно перенесена' })
        return true
      } catch (error) {
        EventLogger.error('moveTransaction', [`Catch: ${(error as Error).message}`])
        return false
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },
  },
})

